import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, DatabaseBackup, Download, Upload, RotateCcw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { KeyPanel } from "@/components/backup/KeyPanel";
import { RestoreDialog } from "@/components/backup/RestoreDialog";
import { dt, size, ORIGIN, showError, saveBlob, uploadBackup } from "@/components/backup/util";

const sel = "h-9 rounded-lg border border-input bg-white px-2 text-sm";

function Warning() {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex gap-3" data-testid="backup-key-warning">
      <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
      <div className="text-sm text-red-900 space-y-1">
        <div className="font-semibold">Şifreleme anahtarı kaybolursa veriler kurtarılamayabilir.</div>
        <p>.emlakplus yedekleri AES-256-GCM ile şifrelenir ve yalnızca EmlakPlus tarafından açılabilir. Sunucu tamamen kaybolursa yedekleri açmanın tek yolu kurtarma anahtarı dosyası ve kurtarma parolasıdır. Bu ikisini sunucudan ayrı, güvenli bir yerde (USB, kasa) saklayın; EmlakPlus bunları sizin yerinize kurtaramaz.</p>
      </div>
    </div>
  );
}

function Row({ b, onRestore, reload }) {
  const download = async () => { try { const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" }); saveBlob(r.data, b.filename); } catch (e) { await showError(e); } };
  const remove = async () => { if (!window.confirm("Yedek kalıcı olarak silinsin mi?")) return; try { await api.delete(`/admin/backups/${b.id}`); reload(); } catch (e) { await showError(e); } };
  return (
    <div className="flex flex-wrap items-center gap-3 p-4 border-b last:border-0" data-testid={`backup-row-${b.id}`}>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold text-slate-900 font-mono truncate">{b.filename}</div>
        <div className="text-xs text-slate-500">{dt(b.created_at)} · {b.created_by} · {size(b.size)}{b.records ? ` · ${b.records} kayıt · ${b.files} belge` : ""}</div>
      </div>
      <Badge variant="outline">{ORIGIN[b.origin]}</Badge>
      {b.origin === "UPLOAD" ? <Badge variant="outline" className="border-amber-300 text-amber-800">Doğrulanmadı</Badge>
        : b.scope && <Badge variant="outline">{b.scope === "PLATFORM" ? "Tüm platform" : b.office_name || "Ofis"}</Badge>}
      <div className="flex gap-1">
        {b.origin !== "UPLOAD" && <Button size="sm" variant="ghost" data-testid={`backup-download-${b.id}`} onClick={download}><Download className="w-4 h-4" /></Button>}
        <Button size="sm" variant="outline" data-testid={`backup-restore-${b.id}`} onClick={() => onRestore(b)}><RotateCcw className="w-4 h-4 mr-1" />Doğrula / Geri yükle</Button>
        <Button size="sm" variant="ghost" className="text-red-600" data-testid={`backup-delete-${b.id}`} onClick={remove}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export default function AdminBackupsPage() {
  const [data, setData] = useState(null);
  const [offices, setOffices] = useState([]);
  const [target, setTarget] = useState("PLATFORM");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const fileRef = useRef();
  const load = () => api.get("/admin/backups").then((r) => setData(r.data)).catch(showError);
  useEffect(() => { load(); api.get("/offices").then((r) => setOffices(r.data)).catch(() => {}); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const body = target === "PLATFORM" ? { scope: "PLATFORM" } : { scope: "OFFICE", office_id: target };
      await api.post("/admin/backups", body); toast.success("Şifreli yedek oluşturuldu"); load();
    } catch (e) { await showError(e); }
    setBusy(false);
  };
  const upload = async (file) => {
    setProgress(0);
    try { const b = await uploadBackup(file, setProgress); toast.success("Dosya EmlakPlus yedeği olarak tanındı"); load(); setRestoring(b); }
    catch (e) { await showError(e); }
    setProgress(null); fileRef.current.value = "";
  };

  return (
    <div className="space-y-6" data-testid="admin-backups-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Yedekleme ve Geri Yükleme</h1>
        <p className="text-slate-500 mt-1">Görsel ve videolar hariç tüm veriler ve belgeler, EmlakPlus'a özel şifreli .emlakplus formatında yedeklenir. Her gece otomatik yedek alınır, son 14 tanesi saklanır.</p>
      </div>
      <Warning />
      <KeyPanel onChanged={load} />
      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <DatabaseBackup className="w-5 h-5 text-emerald-700" />
          <h2 className="font-semibold text-slate-900 mr-auto" style={{ fontFamily: "Outfit, sans-serif" }}>Yedekler</h2>
          <select className={sel} data-testid="backup-target" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="PLATFORM">Tüm platform</option>
            {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <Button data-testid="backup-create-btn" disabled={busy || !data?.recovery_ready} onClick={create}>{busy ? "Şifreleniyor..." : "Şimdi yedek al"}</Button>
          <Button variant="outline" data-testid="backup-upload-btn" disabled={progress !== null} onClick={() => fileRef.current.click()}><Upload className="w-4 h-4 mr-1.5" />.emlakplus yükle</Button>
          <input ref={fileRef} type="file" accept=".emlakplus" className="hidden" data-testid="backup-upload-input" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </div>
        {data && !data.recovery_ready && <p className="text-sm text-red-700" data-testid="backup-need-kit">Yedek alabilmek için önce kurtarma anahtarı oluşturun.</p>}
        {progress !== null && <div data-testid="backup-upload-progress"><Progress value={progress} /><div className="text-xs text-slate-500 mt-1">Yükleniyor %{progress}</div></div>}
        <div className="rounded-xl border overflow-hidden">
          {!data ? <div className="p-6 text-sm text-slate-400">Yükleniyor...</div> : data.items.length === 0
            ? <div className="p-8 text-center text-sm text-slate-400">Henüz yedek yok</div>
            : data.items.filter((b) => b.status === "READY").map((b) => <Row key={b.id} b={b} reload={load} onRestore={setRestoring} />)}
        </div>
      </Card>
      <RestoreDialog backup={restoring} onClose={() => setRestoring(null)} onDone={load} />
    </div>
  );
}
